import { Check, ListPlus, Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../../../components/Button';
import { GroupCard } from '../../../../components/GroupCard';
import { Tooltip } from '../../../../components/Tooltip';
import { CountBadge } from '../../../../components/CountBadge';
import { ProgressPie } from '../../../../components/ProgressPie';
import { StatusText } from '../../../../components/table/StatusText';
import { Modal } from '../../../../components/Modal';
import { Stepper } from '../../../../components/Stepper';
import { Textarea } from '../../../../components/inputs/Textarea';
import { TextField } from '../../../../components/inputs/TextField';
import { DateField } from '../../../../components/inputs/DateField';
import { Select, type SelectOption } from '../../../../components/inputs/Select';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import {
  InlineAddTable,
  InlineRow,
} from '../../../../components/inline-add-table/InlineAddTable';
import {
  TdMoney,
  TdNumber,
  TdSeamless,
  TdSelect,
} from '../../../../components/inline-add-table/cells';
import { usePermission } from '../../../../auth/AuthContext';
import { ApiError } from '../../../../lib/apiError';
import { formatMoney } from '../../../../lib/formatMoney';
import { toCalendarDate } from '../../../../components/calendar/monthGrid';
import { uid } from '../../../../lib/uid';
import { focusValidationError } from '../../../../lib/validationFocus';
import { purchaseOrdersApi } from '../purchaseOrdersApi';
import { suppliersApi } from '../suppliersApi';
import type {
  OrderablePurchaseRequest,
  OrderableRequestLine,
  PurchaseOrderDetail,
  PurchaseOrderLineInput,
} from '../purchaseOrderTypes';
import type { SupplierOption } from '../supplierTypes';

const ID_PREFIX = 'purchase-order';

/** The three steps, in the order the source's own form has them. */
const STEPS = [
  { key: 'sources', label: 'Source PRs' },
  { key: 'details', label: 'Order Details' },
  { key: 'summary', label: 'Summary' },
];

/** One supplier group — each becomes its own purchase order. */
interface OrderGroup {
  id: string;
  supplierId: string | null;
  expectedDeliveryDate: string;
  paymentTerms: string;
  notes: string;
  /** The lines routed to this supplier. A request line may be split across several groups. */
  lines: GroupLine[];
}

interface GroupLine {
  key: string;
  purchaseRequestItemId: string | null;
  orderedQuantity: number | null;
  unitCost: number | null;
}

function newGroup(): OrderGroup {
  return {
    id: uid(),
    supplierId: null,
    expectedDeliveryDate: '',
    paymentTerms: '',
    notes: '',
    lines: [],
  };
}

function newLine(): GroupLine {
  return { key: uid(), purchaseRequestItemId: null, orderedQuantity: null, unitCost: null };
}

/**
 * Place or correct a purchase order.
 *
 * **ONE PURCHASE ORDER MAY BE COMPOSED OF SEVERAL PURCHASE REQUESTS, and step one is where that
 * happens.** Product call, 29 Sep 2026. The source's own step one is a single-select card list;
 * here the same cards carry a checkbox each, their still-orderable lines POOL together, and step
 * two routes that pool to one or more suppliers. Nothing else about the flow changed — an order
 * per supplier is still what a split produces.
 *
 * **THE CREATE WRITES ONE ORDER PER SUPPLIER GROUP, SEQUENTIALLY.** Each is its own record with
 * its own approval, so a partial failure is reportable per order rather than leaving one request
 * half-written — and the form says how many landed. The source does the same.
 *
 * **AN EDIT IS ONE SUPPLIER AND THE LINES THE ORDER ALREADY HAS.** Adding a line to a placed
 * order would need the whole pool again with this order's own commitment added back to every
 * cap, which is a second flow over one form; a new line is a new order. Stated on the form
 * rather than left to be discovered.
 */
export function PurchaseOrderModal({
  open,
  editing,
  initialRequestId,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: PurchaseOrderDetail | null;
  /**
   * Pre-ticks ONE source request — the hand-off from that request's own profile.
   *
   * **IT SEEDS, IT DOES NOT LOCK.** Product call, 05 Oct 2026. The reader answered step one by
   * pressing Create purchase order on a particular request, so the card arrives ticked and its
   * lines are already in the pool — and they may still tick a second request beside it, because
   * one order drawing on several requests is the capability this module was built for. Locking
   * to one would remove it. That is the call the issuance form's own seeded item takes, and the
   * opposite of the org chart's locked parent, where no such capability exists to lose.
   *
   * **IT LANDS ON STEP ONE, not step two.** Two reasons, and the first is a race: the pool is
   * filled by the `orderable-requests` feed, which has not landed on the render where the modal
   * opens — so step two would draw an empty grid for as long as the fetch takes. The second is
   * that the tick is the reader's confirmation the hand-off targeted the request they meant.
   * The stated cost is one click.
   *
   * **A `useState` initializer is enough because the modal REMOUNTS**: every call site renders
   * it behind `{creating ? … : null}` rather than leaving it mounted, so there is no stale
   * selection to reset and no effect to write. A call site that starts leaving it mounted owes
   * one.
   *
   * **IGNORED ON AN EDIT**, which is not a defence but a definition: an edit holds the one order
   * it is correcting and its lines, and never reads the pool at all.
   *
   * **A request that is no longer orderable degrades to nothing.** The id is held whatever the
   * feed says, and `pool` keeps only what the feed returned AND flagged orderable — so a request
   * ordered out from under the reader leaves step one with nothing ticked rather than a card that
   * cannot be used. The server is still the gate.
   */
  initialRequestId?: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  /*
    WHETHER THE READER MAY SUBMIT decides what the form promises, not what it does. Without the
    key the order is created as a Draft and an authorized reader submits it later — so the
    summary says which of the two is about to happen rather than offering a tick that does
    nothing.
  */
  const canSubmit = usePermission('purchase-orders.submit');

  const [step, setStep] = useState(0);

  const [requests, setRequests] = useState<OrderablePurchaseRequest[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [selectedRequestIds, setSelectedRequestIds] = useState<string[]>(() =>
    initialRequestId && editing === null ? [initialRequestId] : [],
  );

  /*
    A SEEDED CARD IS SCROLLED INTO VIEW, which is the rule `<Select>` already follows for a
    chosen option below the fold — and the list is what forced it here, exactly as the year
    picker forced it there.

    MEASURED, not guessed: the hand-off from PR-26-00001 seeded the FIFTH card of seven, which
    sits at 813–894 in a list clipped at 835. The tick was correct and entirely invisible, so a
    reader opened the modal, saw nothing ticked, and had no way to tell the hand-off had worked
    from the one thing it was supposed to confirm.

    IT TOUCHES THE LIST AND NOTHING ELSE. `scrollIntoView` scrolls every ancestor scroll
    container, which inside a dialog can move the dialog; setting `scrollTop` from the two
    rects moves exactly the one container that needs to move.

    ONCE, and only on the way in. It fires when the feed lands — the card does not exist before
    that — and the latch is what stops it dragging the list back under a reader who has since
    scrolled somewhere else.
  */
  const sourceListRef = useRef<HTMLUListElement | null>(null);
  const seededCardRef = useRef<HTMLLIElement | null>(null);
  const seedScrolled = useRef(false);

  useEffect(() => {
    if (seedScrolled.current || !initialRequestId) return;

    const list = sourceListRef.current;
    const card = seededCardRef.current;
    if (!list || !card) return;

    seedScrolled.current = true;

    const delta = card.getBoundingClientRect().top - list.getBoundingClientRect().top;
    if (delta < 0 || delta + card.offsetHeight > list.clientHeight) {
      // A small lead so the card reads as part of the list rather than flush against its edge.
      list.scrollTop += delta - 8;
    }
  }, [requests, initialRequestId]);
  const [search, setSearch] = useState('');

  /*
    WHICH HALF OF THE QUEUE IS ON SCREEN — product call, 02 Oct 2026, in the reader's own words:
    *add also a filter, All, Pending, Approved*.

    It is a VIEW SCOPE over the feed, not a facet: there is nothing paged behind it, the whole
    list arrives in one call, and what the reader is choosing is which half of it to look at.
    `All` is the resting state, because the point of showing the pending queue is that a reader
    composing an order sees what is coming.
  */
  const [sourceFilter, setSourceFilter] = useState<'all' | 'pending' | 'approved'>('all');

  const [groups, setGroups] = useState<OrderGroup[]>(() => [newGroup()]);

  /*
    WHICH CARDS ARE SHUT — product call, 02 Oct 2026, in the reader's own words: *make the
    supplier group collapsible/expandable*. A pool split across three suppliers is three tall
    cards, and a reader correcting the third scrolls past two they have already finished.

    **COLLAPSED ids rather than expanded ones**, so a group the reader has just added is open
    with nothing having to remember to open it — which is what makes `Add another supplier`
    land on a card they can type into.

    It is per-session state and is NOT persisted, which is the Show-deleted call rather than
    the column-width one: a remembered collapse hides a card the reader has to fill in before
    the form can be submitted, on a form they have only just opened.
  */
  const [collapsedGroups, setCollapsedGroups] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const toggleGroup = (id: string) =>
    setCollapsedGroups((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  // Edit mode holds ONE order: its supplier, its header, and the lines it already has.
  const [supplierId, setSupplierId] = useState<string | null>(editing?.supplierId ?? null);
  const [delivery, setDelivery] = useState(editing?.expectedDeliveryDate ?? '');
  const [terms, setTerms] = useState(editing?.paymentTerms ?? '');
  const [notes, setNotes] = useState(editing?.notes ?? '');

  const [editLines, setEditLines] = useState<GroupLine[]>(
    editing
      ? editing.items
          .filter((l) => l.isActive)
          .map((l) => ({
            key: l.id,
            purchaseRequestItemId: l.purchaseRequestItemId,
            /*
              `?? null` AT THE SEAM, because this is where a WIRE row becomes a FORM row. The API
              omits a null property entirely, so a value the server did not send arrives as
              `undefined` while the row type declares `number | null` — and `undefined * qty` is
              NaN, which is the defect the purchase-request modal already shipped once.
            */
            orderedQuantity: l.orderedQuantity ?? null,
            unitCost: l.unitCost ?? null,
          }))
      : [],
  );

  const [submitNow, setSubmitNow] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const isEdit = editing !== null;

  useEffect(() => {
    const controller = new AbortController();

    // Both feeds FAIL SOFT: a failed one leaves its picker empty and the rest of the form
    // working, rather than failing the whole dialog.
    suppliersApi
      .options(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setSuppliers(next);
      })
      .catch(() => {});

    if (!isEdit) {
      purchaseOrdersApi
        .orderableRequests(controller.signal)
        .then((next) => {
          if (!controller.signal.aborted) setRequests(next);
        })
        .catch(() => {});
    }

    return () => controller.abort();
  }, [isEdit]);

  // --- The pool ---

  /*
    EVERY STILL-ORDERABLE LINE OF EVERY SELECTED REQUEST, flattened. This IS the multi-request
    change: step two knows nothing about which request a line came from except what the option's
    own sublabel says, so routing lines from two requests to one supplier needs no special case.
  */
  const pool = useMemo(() => {
    const chosen = new Set(selectedRequestIds);

    /*
      `isOrderable` IS TESTED HERE TOO, which is the one funnel into every one of step two's
      pickers, caps and seeds. A pending card is not a button, so nothing can put its id in
      `selectedRequestIds` — this is what keeps that true if a second writer is ever added, and
      it is cheap because the flag is already on the row. The SERVER is still the gate:
      `ResolveLinesAsync` refuses a line of a request an order may not draw on whatever the
      picker allowed, so this keeps the reader out of a refusal rather than being the rule.
    */
    return requests
      .filter((r) => chosen.has(r.id) && r.isOrderable)
      .flatMap((r) => r.lines.map((line) => ({ request: r, line })));
  }, [requests, selectedRequestIds]);

  /*
    WHAT THE LIST DRAWS — the filter and the search, in that order. Both narrow what is on
    screen and neither touches the feed, which arrives whole in one call.
  */
  const visibleRequests = useMemo(
    () =>
      requests.filter((r) => {
        if (sourceFilter === 'pending' && r.isOrderable) return false;
        if (sourceFilter === 'approved' && !r.isOrderable) return false;

        return matches(r, search);
      }),
    [requests, sourceFilter, search],
  );

  const poolByLineId = useMemo(
    () => new Map(pool.map((entry) => [entry.line.purchaseRequestItemId, entry])),
    [pool],
  );

  /** How much of a request line every group has taken, optionally ignoring one group. */
  const takenOf = useCallback(
    (lineId: string, exceptGroupId?: string) =>
      groups.reduce((sum, group) => {
        if (group.id === exceptGroupId) return sum;

        return (
          sum
          + group.lines
            .filter((l) => l.purchaseRequestItemId === lineId)
            .reduce((n, l) => n + (l.orderedQuantity ?? 0), 0)
        );
      }, 0),
    [groups],
  );

  /** What one group may still take of a line — the remainder minus what the OTHERS took. */
  const capFor = useCallback(
    (lineId: string, groupId: string) => {
      const entry = poolByLineId.get(lineId);
      if (!entry) return 0;

      return Math.max(0, entry.line.remainingQuantity - takenOf(lineId, groupId));
    },
    [poolByLineId, takenOf],
  );

  const unrouted = pool.filter(
    (entry) => entry.line.remainingQuantity - takenOf(entry.line.purchaseRequestItemId) > 0,
  );

  // --- Derived validity ---

  const groupLineTotal = (line: GroupLine) => (line.orderedQuantity ?? 0) * (line.unitCost ?? 0);

  const groupTotal = (group: OrderGroup) =>
    group.lines.reduce((sum, line) => sum + groupLineTotal(line), 0);

  /** A group with at least one filled line is an order that will be created. */
  const orderable = groups.filter((g) =>
    g.lines.some((l) => l.purchaseRequestItemId !== null && (l.orderedQuantity ?? 0) > 0),
  );

  const grandTotal = orderable.reduce((sum, g) => sum + groupTotal(g), 0);

  const everyGroupHasSupplier = orderable.every((g) => g.supplierId !== null);

  /*
    EVERY ORDER STATES WHEN IT IS EXPECTED — product call, 01 Oct 2026, in the reader's own
    words: *make expected delivery required*. It was optional on both forms, so an order could
    be placed with nothing to chase it against and nothing for a goods receipt to be early or
    late to.

    **ORDERABLE groups only.** A group a reader has started and not routed a line to yet is not
    an order that will be created, so holding the wizard on its empty date would refuse a step
    over a card that writes nothing.

    The SERVER requires it too — a form's own check is a convenience the endpoint cannot depend
    on, and the column stays NULLABLE so the orders written before this keep what they have.
  */
  const everyGroupHasDelivery = orderable.every((g) => g.expectedDeliveryDate !== '');

  const everyLineFilled = groups.every((g) =>
    g.lines.every(
      (l) =>
        (l.purchaseRequestItemId === null && l.orderedQuantity === null && l.unitCost === null)
        || (l.purchaseRequestItemId !== null && (l.orderedQuantity ?? 0) > 0 && l.unitCost !== null),
    ),
  );

  const editFilled = editLines.filter(
    (l) => l.purchaseRequestItemId !== null && (l.orderedQuantity ?? 0) > 0,
  );

  /*
    NO ORDER MAY COMMIT MORE THAN ITS REQUESTS HAVE LEFT — product call, 01 Oct 2026, in the
    reader's own words: *the PO to be created must not exceed the total of the combined PRs*.

    The Qty cell clamps to the same cap and every seed now reads it, so this is unreachable
    through the form. It is here because **each group is its own POST, sent one after another**:
    an overcommitment that did get through would create the first order and be refused on the
    second, leaving a partial write this form cannot undo. The server re-checks it too, which is
    what closes the race between the feed and the save; this is what keeps the reader out of it.
  */
  const overCommitted =
    pool.find(
      (entry) => takenOf(entry.line.purchaseRequestItemId) > entry.line.remainingQuantity,
    ) ?? null;

  const withinCap = overCommitted === null;

  const sourcesValid = isEdit || selectedRequestIds.length > 0;

  const detailsValid = isEdit
    ? supplierId !== null
      && delivery !== ''
      && editFilled.length > 0
      && editFilled.length === editLines.length
    : orderable.length > 0
      && everyGroupHasSupplier
      && everyGroupHasDelivery
      && everyLineFilled
      && withinCap;

  const canSave = sourcesValid && detailsValid && confirmed && !saving;

  const isLastStep = step === STEPS.length - 1;

  const nextReason =
    step === 0 && !sourcesValid
      ? 'Pick at least one purchase request to order against.'
      : step === 1 && !detailsValid
        ? isEdit
          ? 'Choose a supplier, say when it is expected, and give every line a quantity and a unit cost.'
          : orderable.length === 0
            ? 'Route at least one line to a supplier.'
            : !everyGroupHasSupplier
              ? 'Choose a supplier for every order.'
              : !everyGroupHasDelivery
                ? 'Say when every order is expected to be delivered.'
                : overCommitted !== null
                ? `The orders together ask for more ${overCommitted.line.itemIdentity} than ${overCommitted.request.requestNumber} has left (${overCommitted.line.remainingQuantity}). Lower a quantity.`
                : 'Give every line a quantity and a unit cost.'
        : null;

  // --- Group mutation ---

  function patchGroup(groupId: string, patch: Partial<Omit<OrderGroup, 'id' | 'lines'>>) {
    setGroups((current) => current.map((g) => (g.id === groupId ? { ...g, ...patch } : g)));
  }

  function patchLine(groupId: string, key: string, patch: Partial<GroupLine>) {
    setGroups((current) =>
      current.map((g) =>
        g.id === groupId
          ? { ...g, lines: g.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)) }
          : g,
      ),
    );
  }

  /** Every pool line this group could still take: headroom left, and not already a row of it. */
  const freeFor = useCallback(
    (groupId: string) => {
      const group = groups.find((g) => g.id === groupId);
      if (!group) return [];

      return pool.filter(
        (entry) =>
          capFor(entry.line.purchaseRequestItemId, groupId) > 0
          && !group.lines.some(
            (l) => l.purchaseRequestItemId === entry.line.purchaseRequestItemId,
          ),
      );
    },
    [groups, pool, capFor],
  );

  /*
    FILL THE ORDER WITH EVERY LINE THE CHOSEN REQUESTS STILL HAVE — product call, 01 Oct 2026,
    in the reader's own words: *include item "Include all PR Item", when clicked load all pr
    items in the PO table*.

    It is the one-at-a-time picker's shortcut rather than a second way of composing an order:
    the rows it writes are the rows picking each line would have written, seeded the same way —
    this group's remaining headroom, and the requester's own estimate.

    **THE BLANK ROWS GO.** The table auto-inserts one on open, so filling a table that still
    holds it would leave an empty row under the ones just added — a form opening into its own
    "every line needs an item" state. A row the reader has already filled in is theirs and
    survives; only the untouched blanks are dropped.
  */
  function includeAllLines(groupId: string) {
    // The SAME refusal the menu entry states — a disabled control is the affordance, not the rule.
    if (groups.find((g) => g.id === groupId)?.supplierId == null) return;

    const additions = freeFor(groupId);
    if (additions.length === 0) return;

    setGroups((current) =>
      current.map((g) => {
        if (g.id !== groupId) return g;

        const kept = g.lines.filter((l) => l.purchaseRequestItemId !== null);

        return {
          ...g,
          lines: [
            ...kept,
            ...additions.map((entry) => ({
              key: uid(),
              purchaseRequestItemId: entry.line.purchaseRequestItemId,
              orderedQuantity: capFor(entry.line.purchaseRequestItemId, groupId),
              unitCost: entry.line.estimatedUnitCost ?? null,
            })),
          ],
        };
      }),
    );
  }

  /*
    PICKING A MERGED OPTION WRITES ONE LINE PER SOURCE, which is the standing rule rather than a
    new one: *2 lines 1 per source, but with merged total* (29 Sep 2026). An order line names
    exactly one request line — it is what carries the trace back and what a cancellation gives
    the quantity to — so a dropdown entry covering two requests cannot collapse into one row.

    So the row the reader was on takes the FIRST source and the rest are appended beside it, each
    seeded with ITS OWN REMAINING HEADROOM and its own requester's estimate. Both halves of the
    merge stay honest: the picker asks about the ITEM, and the order still records which request
    each quantity answers.
  */
  function chooseRequestLine(groupId: string, key: string, next: string) {
    const sources = next.startsWith(MULTI_PREFIX)
      ? pool.filter(
          (entry) =>
            entry.line.inventoryItemId === next.slice(MULTI_PREFIX.length)
            && capFor(entry.line.purchaseRequestItemId, groupId) > 0,
        )
      : pool.filter((entry) => entry.line.purchaseRequestItemId === next);

    if (sources.length === 0) return;

    const [first, ...rest] = sources;

    setGroups((current) =>
      current.map((g) => {
        if (g.id !== groupId) return g;

        const patched = g.lines.map((l) =>
          l.key === key
            ? {
                ...l,
                purchaseRequestItemId: first.line.purchaseRequestItemId,

                /*
                  WHAT THIS ORDER MAY STILL TAKE — the request's remainder LESS what the other
                  orders in this session have already taken. It is the same figure the picker
                  states and the Qty cell caps at, so a reader who accepts the seed cannot
                  overcommit the line; seeding the server's whole remainder here is what let two
                  orders in one session each claim the same quantity. A figure the reader has
                  already typed is theirs and survives.
                */
                orderedQuantity:
                  l.orderedQuantity ?? capFor(first.line.purchaseRequestItemId, groupId),

                // THE REQUESTER'S ESTIMATE seeds the agreed cost, and only over an empty cell.
                unitCost: l.unitCost ?? first.line.estimatedUnitCost ?? null,
              }
            : l,
        );

        return {
          ...g,
          lines: [
            ...patched,
            ...rest.map((entry) => ({
              key: uid(),
              purchaseRequestItemId: entry.line.purchaseRequestItemId,
              orderedQuantity: capFor(entry.line.purchaseRequestItemId, groupId),
              unitCost: entry.line.estimatedUnitCost ?? null,
            })),
          ],
        };
      }),
    );
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      if (isEdit) {
        await purchaseOrdersApi.update(editing.id, {
          supplierId: supplierId!,
          expectedDeliveryDate: delivery || null,
          paymentTerms: terms.trim() || null,
          notes: notes.trim() || null,
          items: editLines
            .filter((l): l is GroupLine & { purchaseRequestItemId: string } =>
              l.purchaseRequestItemId !== null,
            )
            .map(toLineInput),
        });

        onSaved();
        return;
      }

      /*
        SEQUENTIAL, one call per supplier group — each order is its own record with its own
        approval. The source does the same, and the reason is the same: an endpoint taking the
        whole split would be one request writing several independent records, and a partial
        failure could not be reported per order.

        A FAILURE STOPS THE RUN and says how many landed. Continuing past one would leave the
        reader with some orders placed, one refused, and a form that looked like it had failed
        entirely.
      */
      let placed = 0;

      for (const group of orderable) {
        await purchaseOrdersApi.create({
          supplierId: group.supplierId!,
          expectedDeliveryDate: group.expectedDeliveryDate || null,
          paymentTerms: group.paymentTerms.trim() || null,
          notes: group.notes.trim() || null,
          items: group.lines
            .filter((l): l is GroupLine & { purchaseRequestItemId: string } =>
              l.purchaseRequestItemId !== null && (l.orderedQuantity ?? 0) > 0,
            )
            .map(toLineInput),
          submitImmediately: canSubmit && submitNow,
        });

        placed += 1;
      }

      void placed;
      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not place this order.', 0);

      setError(failure);

      /*
        A REFUSAL EXPANDS EVERY CARD, and that is not tidiness. `focusValidationError` looks
        the rejected field up by DOM id, and a COLLAPSED group's inputs are unmounted — so the
        reader would be told which cell is wrong while nothing moved, which is the exact
        failure that helper's own rule names. Opening them all is what keeps the focus
        reachable, and the helper already waits two animation frames for precisely this: the
        input is not in the DOM until the render that follows this state change.
      */
      setCollapsedGroups(new Set());

      focusValidationError(failure, { idPrefix: ID_PREFIX });
    } finally {
      setSaving(false);
    }
  }

  /**
   * Fills the form with a real, submittable order.
   *
   * **It resolves against the LIVE feeds** — a real request, real lines at quantities those
   * lines actually have left, and a real supplier — so the primary action enables on the first
   * try rather than producing a request the server refuses.
   *
   * **It leaves the SOURCE REQUESTS alone once the reader has chosen some**, which is the
   * generator's own "never a field the reader has already decided": the selection reveals the
   * pool that step two is built from, so moving it would rebuild the form under them.
   */
  function generate() {
    if (isEdit) {
      // An EDIT is a correction: every required field already arrives filled from the record, so
      // there is nothing for a generator to do — and overwriting the agreed costs would destroy
      // the thing the reader opened the form to read.
      return;
    }

    /*
      ORDERABLE ONLY. The feed lists the pending queue too, and a generator that picked one
      would fill in a form the server then refuses — which is the one thing Autogenerate
      exists to prevent. `isOrderable` is the same flag the card reads, so the picker and the
      generator cannot disagree about which requests are fair game.
    */
    const usable = requests.filter(
      (r) => r.isOrderable && r.lines.some((l) => l.remainingQuantity > 0),
    );

    if (usable.length === 0 || suppliers.length === 0) return;

    const chosen =
      selectedRequestIds.length > 0
        ? usable.filter((r) => selectedRequestIds.includes(r.id))
        : [usable[Math.floor(Math.random() * usable.length)]];

    if (chosen.length === 0) return;

    setSelectedRequestIds(chosen.map((r) => r.id));

    const supplier = suppliers[Math.floor(Math.random() * suppliers.length)];

    /*
      A DELIVERY DATE, because the field is REQUIRED and the generator was not updated when it
      became so — product call, 01 Oct 2026, *make expected delivery required*. Without it
      Autogenerate filled the supplier and every line and left Next dead, with the one empty
      field a step away from the button that would not enable.

      It is the failure CLAUDE.md names in its own words: *a new required field the generator
      does not fill silently breaks the button — and nothing fails, which is why this is the
      check that gets skipped.* The comment on `unitCost` four lines below makes exactly this
      argument for exactly this reason.

      NOON, never midnight, and built from LOCAL y/m/d parts — the calendar's own discipline. In
      a zone whose DST transition lands at midnight that hour does not exist, and the browser
      settles on the day either side.
    */
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 30);
    dueDate.setHours(12, 0, 0, 0);

    setGroups([
      {
        ...newGroup(),
        supplierId: supplier.id,
        expectedDeliveryDate: toCalendarDate(dueDate),
        paymentTerms: '30 days',
        lines: chosen
          .flatMap((r) => r.lines)
          .filter((l) => l.remainingQuantity > 0)
          .map((l) => ({
            key: uid(),
            purchaseRequestItemId: l.purchaseRequestItemId,
            orderedQuantity: l.remainingQuantity,

            /*
              THE REQUESTER'S ESTIMATE where there is one, and a plausible figure where there is
              not — the cost is REQUIRED on an order, so leaving it null would produce a form the
              primary action stays disabled on, which is the one thing the generator exists to
              prevent.
            */
            unitCost: l.estimatedUnitCost ?? 250,
          })),
      },
    ]);

    setStep(1);
  }

  const supplierOptions = suppliers.map((s) => ({
    value: s.id,
    label: s.name,
    sublabel: s.code,
  }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? `Edit ${editing.orderNumber}` : 'Place purchase order'}
      size="3xl"
      id={`${ID_PREFIX}-modal`}
      /*
        AUTOGENERATE IS WITHHELD ON AN EDIT, which is `<ResetUserPasswordModal>`'s exemption read
        for a correction: every required field arrives filled from the record, so the contract is
        already met — and generating over an agreed unit cost would destroy the thing the reader
        came to read.
      */
      autogenerate={isEdit ? undefined : { onGenerate: generate, disabled: saving }}
      /*
        THE TICK IS ON THE LAST STEP ONLY. It certifies the whole transaction, and offering it on
        step one would invite ticking before the reader has seen what is on step two — the strip
        appearing IS the signal that there is nothing left to fill in.
      */
      confirm={
        isLastStep
          ? {
              id: `${ID_PREFIX}-confirm`,
              checked: confirmed,
              onChange: setConfirmed,
              disabled: saving,
            }
          : undefined
      }
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          {step > 0 ? (
            <Button
              id={`${ID_PREFIX}-back`}
              variant="secondary"
              onClick={() => {
                // Re-armed on every step change: a still-ticked acknowledgement would leave the
                // primary live the instant navigation lands back on the last step.
                setConfirmed(false);
                setStep((s) => s - 1);
              }}
              disabled={saving}
            >
              Back
            </Button>
          ) : null}

          {!isLastStep ? (
            /*
              THE REASON GOES IN A `<Tooltip disabledReason>`, not on the Button — a disabled
              element fires no pointer events, so its own `title` would never appear at all. The
              tooltip wraps it in a focusable span, which is what makes "every disabled control
              says why" reachable by keyboard as well.

              NO `block`: the child is a `<Button>`, which is `inline-flex shrink-0`, and `block`
              would make the anchor `w-full` and take the whole footer row.
            */
            <Tooltip disabledReason={nextReason}>
              <Button
                id={`${ID_PREFIX}-next`}
                variant="secondary"
                onClick={() => {
                  setConfirmed(false);
                  setStep((s) => s + 1);
                }}
                disabled={saving || nextReason !== null}
              >
                Next
              </Button>
            </Tooltip>
          ) : (
            <Button
              id={`${ID_PREFIX}-submit`}
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Stepper steps={STEPS} activeIndex={step} />

        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/* ── Step 1 · Source purchase requests ── */}
        {step === 0 ? (
          isEdit ? (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-slate-500">
                The source requests cannot be changed on an existing order — they are whatever its
                lines fulfil. Correct the lines on the next step, or place a new order.
              </p>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="font-mono text-xs text-slate-700">
                  {editing.sourceRequests.map((r) => r.requestNumber).join(', ') || 'No source request'}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <p className="text-xs text-slate-500">
                Pick one or more approved requests. Their still-orderable lines pool together on
                the next step, so <strong>one order can cover several requests</strong>. Requests
                still awaiting a decision are listed and cannot be picked.
              </p>

              <TextField
                id={`${ID_PREFIX}-search`}
                label="Search requests"
                labelHidden
                description="Narrows the list below by request number, item or category."
                placeholder="Search purchase requests…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={saving}
              />

              {/*
                ALL · PENDING · APPROVED — the reader's own three. It is the pill strip
                `<ReceiveDeliveryModal>` already draws over its own picker, class for class, so
                the two wizards' source steps read as one control rather than as two takes on it:
                the selected pill carries the product's selection treatment (a tint and the
                darkest text), never a hue on the label.

                NO COUNTS, which is that precedent's own call. The strip says which half is on
                screen; how many are in the other half is a question the other pill answers.
              */}
              <div className="flex gap-2">
                {(
                  [
                    ['all', 'All'],
                    ['pending', 'Pending'],
                    ['approved', 'Approved'],
                  ] as const
                ).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    id={`${ID_PREFIX}-source-filter-${key}`}
                    onClick={() => setSourceFilter(key)}
                    disabled={saving}
                    aria-pressed={sourceFilter === key}
                    className={
                      sourceFilter === key
                        ? 'rounded-full border border-slate-400 bg-blue-50 px-3 py-1 text-xs font-medium text-slate-900'
                        : 'rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600 hover:bg-slate-50'
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>

              {visibleRequests.length === 0 ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-8 text-center text-xs text-slate-500">
                  {/*
                    A SEARCH THAT MATCHED NOTHING SAYS SO, and it is a different sentence from a
                    queue with nothing in it. The block used to render the empty message only
                    when the FEED was empty, so an unmatched search fell through to an empty
                    `<ul>` — nothing at all on screen, which reads as a list that failed to load.
                  */}
                  {requests.length > 0 && search.trim() !== ''
                    ? 'No request matches that search.'
                    : sourceFilter === 'pending'
                      ? 'No request is awaiting a decision.'
                      : sourceFilter === 'approved'
                        ? 'No approved request has anything left to order.'
                        : 'No purchase request has anything left to order. A request has to be approved before an order can draw on it.'}
                </div>
              ) : (
                <ul
                  ref={sourceListRef}
                  className="modern-scrollbar flex max-h-[380px] flex-col gap-2 overflow-y-auto p-0.5"
                >
                  {visibleRequests.map((request) => {
                    const picked = selectedRequestIds.includes(request.id);

                    /*
                      THE CARD'S OWN CONTENT IS THE SAME EITHER WAY, which is what keeps the two
                      halves of the list readable as one list: the same identity line, the same
                      summary, the same pie at the outer end. What differs is the WRAPPER — a
                      pending request is a `<div>` rather than a `<button>` — and the badge.
                    */
                    const body = (
                      <>
                        <span
                          aria-hidden="true"
                          className={
                            request.isOrderable
                              ? `mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                                  picked
                                    ? 'border-brand-700 text-brand-700'
                                    : 'border-slate-500 text-transparent'
                                }`
                              : /*
                                  THE SLOT IS RESERVED AND EMPTY on a pending card. An empty box
                                  would read as a checkbox the reader may tick, and closing the
                                  slot up would step every pending card's title left of the ones
                                  above and below it — the ragged-column cost, down a list this
                                  time rather than across a row.
                                */
                                'mt-0.5 h-4 w-4 shrink-0'
                          }
                        >
                          {request.isOrderable ? <Check className="h-3 w-3" /> : null}
                        </span>

                        <span className="min-w-0 flex-1">
                          {/*
                            THE STATUS IS BESIDE THE NUMBER AGAIN — 02 Oct 2026, reversing the
                            01 Oct call that took it off. That call was exact for the list it was
                            made on: the feed offered only approved requests, so "Approved" was
                            true of every card and distinguished none of them. With the pending
                            queue in the list it is the ONE thing separating a card the reader
                            may tick from a card they may only look at.

                            THE URGENCY DID NOT COME BACK. It is still a fact about the request
                            rather than about what is left to order on it, and nothing here asks
                            for it.

                            The TONE is the one distinction this list cares about — orderable or
                            not — and the LABEL carries the finer state, which is what keeps
                            "Approved" and "Partially ordered" from sharing one word. Amber is
                            legal here because this is a modal: the no-yellow rule is about an
                            index page's own rows.
                          */}
                          <span className="flex items-center gap-2">
                            <span className="font-mono text-sm font-medium text-slate-900">
                              {request.requestNumber}
                            </span>

                            <StatusText tone={request.isOrderable ? 'active' : 'pending'}>
                              {request.statusLabel}
                            </StatusText>
                          </span>

                          <span className="mt-0.5 block truncate text-xs text-slate-600">
                            {request.items ?? 'No items'}
                          </span>

                          <span className="mt-0.5 block text-xs text-slate-500">
                            {request.lines.length} line
                            {request.lines.length === 1 ? '' : 's'} left to order
                            {/*
                              `!= null`, never `!==`: the API omits a null property entirely, so
                              an unpriced request arrives as `undefined` and the strict test was
                              TRUE for it — printing `est. —` on every request nobody had priced.
                            */}
                            {request.remainingEstimatedTotal != null
                              ? ` · est. ${formatMoney(request.remainingEstimatedTotal)}`
                              : ''}
                            {request.deliverToName ? ` · to ${request.deliverToName}` : ''}
                          </span>

                          {/*
                            WHY IT CANNOT BE PICKED, on the card rather than in a tooltip on a
                            control that is not there. It names the missing STEP — the shape
                            `<ReceiveDeliveryModal>`'s own gate states — so a reader meets the
                            reason where they meet the row.
                          */}
                          {request.isOrderable ? null : (
                            <span className="mt-1 block text-xs text-amber-700">
                              Awaiting a decision — an order can draw on it once it is approved.
                            </span>
                          )}
                        </span>

                        {/*
                          HOW FAR THROUGH THE REQUEST IS, at the outer end — the one fact that
                          differs card to card among the ones a reader may pick. It reads the
                          request's OWN line counts rather than `lines.length`, which is the
                          remainder: the feed drops a covered line, so counting it would report
                          every request as untouched.
                        */}
                        <ProgressPie
                          value={request.orderedLineCount}
                          total={request.totalLineCount}
                          label="lines ordered"
                          className="shrink-0 self-center"
                        />
                      </>
                    );

                    return (
                      <li
                        key={request.id}
                        ref={request.id === initialRequestId ? seededCardRef : undefined}
                      >
                        {request.isOrderable ? (
                          <button
                            type="button"
                            id={`${ID_PREFIX}-source-${request.id}`}
                            onClick={() =>
                              setSelectedRequestIds((current) =>
                                picked
                                  ? current.filter((x) => x !== request.id)
                                  : [...current, request.id],
                              )
                            }
                            disabled={saving}
                            aria-pressed={picked}
                            className={`flex w-full items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition ${
                              picked
                                ? 'border-slate-400 bg-blue-50'
                                : 'border-slate-200 bg-white hover:bg-slate-50'
                            }`}
                          >
                            {body}
                          </button>
                        ) : (
                          /*
                            NOT A DISABLED BUTTON — product call, 02 Oct 2026, in the reader's own
                            words: *dont make it selectable*. A disabled control promises it could
                            become actionable from here and owes a reason in a tooltip; nothing
                            the reader does on this step approves a request, so the card is not a
                            control at all. It is a row of the queue, and the sentence inside it
                            is where the reason reads.
                          */
                          <div className="flex w-full items-start gap-3 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2.5 text-left">
                            {body}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              {selectedRequestIds.length > 1 ? (
                <p className="text-xs text-slate-600">
                  <strong>{selectedRequestIds.length} requests</strong> selected — their lines pool
                  together, and one order can draw on all of them.
                </p>
              ) : null}
            </div>
          )
        ) : null}

        {/* ── Step 2 · Order details ── */}
        {step === 1 ? (
          isEdit ? (
            <div className="flex flex-col gap-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Select
                  id={`${ID_PREFIX}-supplier-id`}
                  label="Supplier"
                  required
                  description="Who the order goes to. Only live suppliers are offered."
                  value={supplierId}
                  onChange={setSupplierId}
                  options={supplierOptions}
                  remote
                  error={error?.fieldError('supplierId')}
                  disabled={saving}
                />

                <DateField
                  id={`${ID_PREFIX}-expected-delivery-date`}
                  label="Expected delivery"
                  required
                  description="When the supplier is expected to deliver — the one date here that may be in the future."
                  value={delivery}
                  onChange={setDelivery}
                  error={error?.fieldError('expectedDeliveryDate')}
                  disabled={saving}
                />
              </div>

              <TextField
                id={`${ID_PREFIX}-payment-terms`}
                label="Payment terms"
                description="What was agreed about paying — 30 days, 50% on delivery. Free text."
                value={terms}
                onChange={(e) => setTerms(e.target.value)}
                error={error?.fieldError('paymentTerms')}
                disabled={saving}
              />

              <p className="text-xs text-slate-500">
                An edit changes the lines this order already has. To order something else, place a
                new order — the remaining quantity stays on the request.
              </p>

              <InlineAddTable
                headers={[
                  /*
                    THE PR NUMBER IS UNDER THE REQUEST LINE, NOT BESIDE IT — product call,
                    02 Oct 2026, in the reader's own words: *remove the PR column, instead merge
                    it with the request line and place the PR no below it*.

                    IT REVERSES THE COLUMN THIS SHIPPED WITH, which read "ITS OWN COLUMN, matching
                    step two, the summary and the profile's Lines tab. It was appended to the item
                    cell, which is a stacked value in a grid: no header, no width, no resize entry,
                    and a different shape from the three surfaces beside it." Three of those four
                    costs are real and are paid: the merged value has no header, no width of its
                    own and no resize entry. What bought them is the 130px the column was spending
                    on a value that REPEATS down it — one order usually draws on one or two
                    requests — handed to the item identity, which is the cell that truncates.

                    THE FOURTH COST IS THE ONE THAT HAD TO BE CLOSED: a shape that differs between
                    the step and its own recap is a reader filling in a merged cell and reading two
                    columns one click later. So the SUMMARY merged with it, in the same change.

                    THE PROFILE'S LINES TAB KEEPS ITS COLUMN, and that divergence is deliberate
                    rather than missed: it is a `<PreviewGrid>` of ten columns that scrolls
                    horizontally, where a column has a header and a width and the room is not being
                    competed for. Stated here so the next reader does not "fix" one to match the
                    other.
                  */
                  'Request line',
                  { label: 'Qty *', align: 'right' },
                  { label: 'Unit cost *', align: 'right' },
                  { label: 'Line total', align: 'right' },
                ]}
                /*
                  THE MERGED COLUMN TAKES THE PR COLUMN'S WIDTH — 430, not 300 — so the grid is
                  the same total width it was and the identity line gains every pixel the
                  separate column was spending.

                  `resizeKey` IS VERSIONED WITH THE COLUMN SET, because stored widths are
                  POSITIONAL: five columns' saved values applied to four is a table that overflows
                  for every reader who has ever dragged one and for nobody who has not. It went to
                  -v2 when the column arrived and goes to -v3 now that it is gone.
                */
                colWidths={[430, 100, 140, 140]}
                resizeKey={`${ID_PREFIX}-edit-lines-v3`}
                // An edit adds no line, so the `+` is dead — see the note above the table.
                rowCount={editLines.length}
                onAddRow={() => {}}
                addDisabled
                addButtonId={`${ID_PREFIX}-edit-add-line`}
                footerContent={
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-xs text-slate-600">
                      Removing a line gives its quantity back to the request.
                    </p>
                    <p className="shrink-0 font-mono text-xs tabular-nums text-slate-600">
                      Total{' '}
                      {formatMoney(
                        editLines.reduce((sum, l) => sum + groupLineTotal(l), 0),
                      )}
                    </p>
                  </div>
                }
              >
                {editLines.map((line, index) => {
                  const source = editing.items.find((i) => i.id === line.key);

                  return (
                    <InlineRow
                      key={line.key}
                      idx={index + 1}
                      onRemove={() =>
                        setEditLines((current) => current.filter((l) => l.key !== line.key))
                      }
                    >
                      {/*
                        STATED, never offered — the line is fixed on an edit, so a picker here
                        would be a control with one legal answer. `<TdSeamless>` is what a
                        read-only fact inside an inline grid takes.
                      */}
                      <TdSeamless
                        below={
                          <span className="font-mono">
                            {source?.purchaseRequestNumber ?? '—'}
                          </span>
                        }
                      >
                        <span title={source?.itemIdentity ?? ''}>{source?.itemIdentity ?? 'Line'}</span>
                      </TdSeamless>

                      <TdNumber
                        id={`${ID_PREFIX}-items.${index}.orderedQuantity`}
                        ariaLabel={`Quantity on line ${index + 1}`}
                        value={line.orderedQuantity}
                        onChange={(next) =>
                          setEditLines((current) =>
                            current.map((l) =>
                              l.key === line.key ? { ...l, orderedQuantity: next } : l,
                            ),
                          )
                        }
                        integerOnly
                        min={1}
                        /*
                          AN EDIT HAS NO CAP IN HAND, so the SERVER is what validates it — this
                          form holds one order's own lines and never loads the pool, and a
                          headroom invented here would be a second answer to a question only the
                          stored request line can settle.

                          What this does is put the server's refusal IN THE CELL it names.
                          `focusValidationError` already brings the reader to `items.N.orderedQuantity`;
                          before this they arrived at a box that said nothing about itself.
                        */
                        invalid={error?.fieldError(`items.${index}.orderedQuantity`) ?? null}
                        disabled={saving}
                      />

                      <TdMoney
                        id={`${ID_PREFIX}-items.${index}.unitCost`}
                        ariaLabel={`Unit cost on line ${index + 1}`}
                        value={line.unitCost}
                        onChange={(next) =>
                          setEditLines((current) =>
                            current.map((l) => (l.key === line.key ? { ...l, unitCost: next } : l)),
                          )
                        }
                        disabled={saving}
                      />

                      <TdSeamless align="right">
                        <span className="font-mono tabular-nums">
                          {formatMoney(groupLineTotal(line))}
                        </span>
                      </TdSeamless>
                    </InlineRow>
                  );
                })}
              </InlineAddTable>

              <Textarea
                id={`${ID_PREFIX}-notes`}
                label="Notes"
                description="A free note about the order as a whole."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                error={error?.fieldError('notes')}
                disabled={saving}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex items-baseline justify-between gap-3">
                <p className="text-xs text-slate-500">
                  Pick a supplier, then add the lines going to it.{' '}
                  <strong>Each supplier becomes its own purchase order</strong>, and one line&apos;s
                  quantity can be split across several — ten as five and five.
                </p>

                <span className="shrink-0 text-xs text-slate-500">
                  {orderable.length} order{orderable.length === 1 ? '' : 's'} ·{' '}
                  {unrouted.length} unrouted
                </span>
              </div>

              {groups.map((group, groupIndex) => {
                /*
                  A SUPPLIER ALREADY CHOSEN BY ANOTHER GROUP IS NOT OFFERED, so one order per
                  supplier per session — two orders to one supplier out of one pool is one order
                  with more lines on it. The source does the same.
                */
                const takenSuppliers = new Set(
                  groups.filter((g) => g.id !== group.id && g.supplierId).map((g) => g.supplierId),
                );

                const supplierName =
                  suppliers.find((x) => x.id === group.supplierId)?.name ?? null;

                const collapsed = collapsedGroups.has(group.id);

                const routedLines = group.lines.filter(
                  (l) => l.purchaseRequestItemId !== null,
                ).length;

                return (
                  /*
                    One card of the supplier stack — the shared <GroupCard> (19 Oct 2026), the
                    shape the plantilla's unit stack uses too: the supplier read down the spine
                    while open, carried in the header with the line count and subtotal while shut.
                  */
                  <GroupCard
                    key={group.id}
                    id={`${ID_PREFIX}-group-${group.id}`}
                    title={`Purchase order ${groupIndex + 1}`}
                    subjectLabel="Supplier"
                    subject={supplierName}
                    collapsed={collapsed}
                    onToggle={() => toggleGroup(group.id)}
                    summary={
                      routedLines > 0 ? (
                        <>
                          {' · '}
                          {routedLines} line{routedLines === 1 ? '' : 's'}
                          {' · '}
                          <span className="font-mono tabular-nums">{formatMoney(groupTotal(group))}</span>
                        </>
                      ) : null
                    }
                    onRemove={
                      groups.length > 1
                        ? () => setGroups((current) => current.filter((g) => g.id !== group.id))
                        : undefined
                    }
                    removeDisabled={saving}
                  >
                      <>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <Select
                          id={`${ID_PREFIX}-group-${group.id}-supplier-id`}
                          label="Supplier"
                          required
                          description="Who this order goes to. A supplier already used by another order here is not offered — that would be one order with more lines."
                          value={group.supplierId}
                          onChange={(next) => patchGroup(group.id, { supplierId: next })}
                          options={supplierOptions.filter((o) => !takenSuppliers.has(o.value))}
                          remote
                          error={
                            group.lines.length > 0 && group.supplierId === null
                              ? 'Choose the supplier this order goes to.'
                              : undefined
                          }
                          disabled={saving}
                        />

                        <DateField
                          id={`${ID_PREFIX}-group-${group.id}-delivery`}
                          label="Expected delivery"
                          required
                          description={`When they are expected to deliver. An order the supplier cannot be held to a date on is one nobody can chase.${
                            group.supplierId === null ? ' Choose this order’s supplier first.' : ''
                          }`}
                          value={group.expectedDeliveryDate}
                          /*
                            IT REDDENS ONCE THE ORDER HAS A LINE SOMEBODY CHOSE, not once it has a
                            ROW. The gate read `lines.length > 0`, which was the same thing until
                            the grid started auto-inserting its blank row — after that, picking a
                            supplier reddened the date beside it on the same click, before the
                            reader had touched anything. Choosing a supplier does not make a
                            delivery date wrong, and a form that opens a field in error is the
                            thing `useDeferredError` exists to stop, arriving one interaction in.
                          */
                          error={
                            group.lines.some((l) => l.purchaseRequestItemId !== null) &&
                            group.expectedDeliveryDate === ''
                              ? 'Say when they are expected to deliver.'
                              : undefined
                          }
                          onChange={(next) => patchGroup(group.id, { expectedDeliveryDate: next })}
                          disabled={saving || group.supplierId === null}
                        />
                      </div>

                      <InlineAddTable
                        headers={[
                          /*
                            THE PR NUMBER IS UNDER THE REQUEST LINE, NOT BESIDE IT — product call,
                            02 Oct 2026, in the reader's own words: *remove the PR column, instead merge
                            it with the request line and place the PR no below it*.

                            IT REVERSES THE COLUMN THIS SHIPPED WITH, which read "ITS OWN COLUMN, matching
                            step two, the summary and the profile's Lines tab. It was appended to the item
                            cell, which is a stacked value in a grid: no header, no width, no resize entry,
                            and a different shape from the three surfaces beside it." Three of those four
                            costs are real and are paid: the merged value has no header, no width of its
                            own and no resize entry. What bought them is the 130px the column was spending
                            on a value that REPEATS down it — one order usually draws on one or two
                            requests — handed to the item identity, which is the cell that truncates.

                            THE FOURTH COST IS THE ONE THAT HAD TO BE CLOSED: a shape that differs between
                            the step and its own recap is a reader filling in a merged cell and reading two
                            columns one click later. So the SUMMARY merged with it, in the same change.

                            THE PROFILE'S LINES TAB KEEPS ITS COLUMN, and that divergence is deliberate
                            rather than missed: it is a `<PreviewGrid>` of ten columns that scrolls
                            horizontally, where a column has a header and a width and the room is not being
                            competed for. Stated here so the next reader does not "fix" one to match the
                            other.
                          */
                          'Request line *',
                          { label: 'Qty *', align: 'right' },
                          { label: 'Unit cost *', align: 'right' },
                          { label: 'Line total', align: 'right' },
                        ]}
                        colWidths={[430, 100, 140, 140]}
                        resizeKey={`${ID_PREFIX}-group-lines-v3`}
                        rowCount={group.lines.length}
                        onAddRow={() =>
                          setGroups((current) =>
                            current.map((g) =>
                              g.id === group.id ? { ...g, lines: [...g.lines, newLine()] } : g,
                            ),
                          )
                        }
                        /*
                          NOTHING IS ADDED BEFORE A SUPPLIER IS CHOSEN — product call, 01 Oct 2026,
                          in the reader's own words: *do not allow adding items if supplier is not
                          yet selected*. An order's lines are what is being bought FROM somebody,
                          and the quantity each line may take is capped per group — so composing one
                          against nobody is filling in an order that does not exist yet.

                          **THE CONSEQUENCE IS THAT THE GRID OPENS EMPTY**, stated rather than
                          discovered: `<InlineAddTable>` honours `addDisabled` when it auto-inserts
                          its blank row, so the table draws its header and a disabled `+` until the
                          supplier is picked, and the first row appears the moment it is. That is
                          the auto-row rule working rather than an exception to it.
                        */
                        addDisabled={saving || group.supplierId === null || unrouted.length === 0}
                        addDisabledReason={
                          group.supplierId === null
                            ? 'Choose this order’s supplier first — a line is something bought from somebody.'
                            : unrouted.length === 0
                              ? 'Every line of the chosen requests is already on an order.'
                              : 'Complete the current row before adding another.'
                        }
                        addMenu={[
                          {
                            key: 'include-all',
                            label: 'Include all PR items',
                            icon: <ListPlus className="h-4 w-4" aria-hidden="true" />,
                            onClick: () => includeAllLines(group.id),
                            disabledReason: saving
                              ? 'Saving.'
                              : group.supplierId === null
                                ? 'Choose this order’s supplier first.'
                                : freeFor(group.id).length === 0
                                  ? 'Every line of the chosen requests is already on an order.'
                                  : null,
                          },
                        ]}
                        addButtonId={`${ID_PREFIX}-group-${group.id}-add-line`}
                        footerContent={
                          <div className="flex items-center justify-between gap-4">
                            <p className="text-xs text-slate-600">
                              One row per request line. A line can appear on two orders.
                            </p>
                            <p className="shrink-0 font-mono text-xs tabular-nums text-slate-600">
                              Subtotal {formatMoney(groupTotal(group))}
                            </p>
                          </div>
                        }
                      >
                        {group.lines.map((line, index) => {
                          const source =
                            line.purchaseRequestItemId === null
                              ? null
                              : (poolByLineId.get(line.purchaseRequestItemId) ?? null);

                          const cap =
                            source === null ? 0 : capFor(source.line.purchaseRequestItemId, group.id);

                          /*
                            THE QUANTITY IS VALIDATED, NOT REWRITTEN — product call, 01 Oct 2026, in
                            the reader's own words: *the qty should validate if the requested
                            quantity is still valid*.

                            It CLAMPED: a figure past the cap was silently replaced by the cap, on
                            the keystroke and again on blur. That is defensible while a ceiling is
                            fixed and dishonest the moment it can MOVE — and here it moves on its
                            own, because the cap is the request's remainder less what the OTHER
                            orders in this wizard have taken. A reader who typed 30 against a cap of
                            45 and then routed 30 of the same line to a second supplier was left
                            holding a figure no order can be given, with the cell saying nothing: the
                            clamp only ever ran while they were typing in THIS box.

                            So the number on screen is theirs, the cell says what is wrong with it,
                            and `canSave` refuses — which is the same guard the footer sentence
                            already states, arriving at the row the reader has to change.
                          */
                          const qtyInvalid =
                            source === null || (line.orderedQuantity ?? 0) <= cap
                              ? null
                              : cap === 0
                                ? `The other orders here already take all ${source.line.remainingQuantity} of ${source.request.requestNumber} that is left.`
                                : `Only ${cap} of ${source.request.requestNumber} is left for this order.`;

                          /*
                            THE OPTIONS ARE WHAT IS LEFT, plus this row's own choice — without the
                            second half a reader who picked a line and took all of it would watch
                            their own selection vanish from the picker they chose it in.
                          */
                          const options = requestLineOptions(
                            pool,
                            group,
                            line,
                            (lineId) => capFor(lineId, group.id),
                          );

                          return (
                            <InlineRow
                              key={line.key}
                              idx={index + 1}
                              onRemove={() =>
                                setGroups((current) =>
                                  current.map((g) =>
                                    g.id === group.id
                                      ? { ...g, lines: g.lines.filter((l) => l.key !== line.key) }
                                      : g,
                                  ),
                                )
                              }
                            >
                              <TdSelect
                                id={`${ID_PREFIX}-items.${index}.purchaseRequestItemId`}
                                ariaLabel={`Request line on row ${index + 1}`}
                                value={line.purchaseRequestItemId}
                                onChange={(next) => chooseRequestLine(group.id, line.key, next)}
                                options={options}
                                remote
                                disabled={saving}
                                /*
                                  THE SOURCE REQUEST, under the line it belongs to — read off the
                                  choice above rather than offered, because the request is a
                                  property of the line already picked. An em dash until one is
                                  picked, which is what the slot HAS to say on a blank row: an
                                  empty one reads as a value that failed to render.
                                */
                                below={
                                  <span className="font-mono">
                                    {source?.request.requestNumber ?? '—'}
                                  </span>
                                }
                              />

                              <TdNumber
                                id={`${ID_PREFIX}-items.${index}.orderedQuantity`}
                                ariaLabel={`Quantity on row ${index + 1}`}
                                value={line.orderedQuantity}
                                onChange={(next) =>
                                  patchLine(group.id, line.key, { orderedQuantity: next })
                                }
                                integerOnly
                                min={1}
                                /*
                                  NO `max`, DELIBERATELY. It clamps on blur, which is the silent
                                  rewrite `qtyInvalid` above replaces — the ceiling here is the one
                                  the reader is being told about rather than one the cell enforces.
                                */
                                invalid={qtyInvalid}
                                disabled={saving || line.purchaseRequestItemId === null}
                              />

                              <TdMoney
                                id={`${ID_PREFIX}-items.${index}.unitCost`}
                                ariaLabel={`Unit cost on row ${index + 1}`}
                                value={line.unitCost}
                                onChange={(next) => patchLine(group.id, line.key, { unitCost: next })}
                                disabled={saving || line.purchaseRequestItemId === null}
                              />

                              <TdSeamless align="right">
                                <span className="font-mono tabular-nums">
                                  {formatMoney(groupLineTotal(line))}
                                </span>
                              </TdSeamless>
                            </InlineRow>
                          );
                        })}
                      </InlineAddTable>

                      {/*
                        THE SUPPLIER GATES EVERY FIELD ON THE CARD, not only the lines — product
                        call, 02 Oct 2026, in the reader's own words: *disable the payment terms,
                        notes, expected delivery if supplier has not yet been selected*.

                        It is the gate the `+` and the fill menu already carry, read one row out:
                        a delivery date is a promise somebody made, terms are what was agreed
                        WITH somebody, and a note is a note about an order that does not exist
                        yet. Filling any of them against nobody is filling in a form for a record
                        the card cannot yet describe.

                        **DISABLED FOR STATE, so each says why** — through its own `description`,
                        which is where the container-then-member picker already puts a sentence
                        like this: these are shared inputs with no `disabledReason` prop, and
                        adding one to carry a sentence the description has a place for is the
                        wrong layer to fix it at. The clause appears only while the gate is shut.
                      */}
                      <TextField
                        id={`${ID_PREFIX}-group-${group.id}-terms`}
                        label="Payment terms"
                        description={`What was agreed about paying — 30 days, 50% on delivery.${
                          group.supplierId === null ? ' Choose this order’s supplier first.' : ''
                        }`}
                        value={group.paymentTerms}
                        onChange={(e) => patchGroup(group.id, { paymentTerms: e.target.value })}
                        disabled={saving || group.supplierId === null}
                      />

                      <Textarea
                        id={`${ID_PREFIX}-group-${group.id}-notes`}
                        label="Notes"
                        description={`A free note about this order.${
                          group.supplierId === null ? ' Choose this order’s supplier first.' : ''
                        }`}
                        value={group.notes}
                        onChange={(e) => patchGroup(group.id, { notes: e.target.value })}
                        disabled={saving || group.supplierId === null}
                      />
                      </>
                  </GroupCard>
                );
              })}

              <Button
                id={`${ID_PREFIX}-add-group`}
                variant="secondary"
                onClick={() => setGroups((current) => [...current, newGroup()])}
                disabled={saving}
                leading={<Plus className="h-4 w-4" aria-hidden="true" />}
              >
                Add another supplier
              </Button>

              {unrouted.length > 0 ? (
                <p className="text-xs text-slate-600">
                  {unrouted.length} line{unrouted.length === 1 ? '' : 's'} still{' '}
                  {unrouted.length === 1 ? 'has' : 'have'} quantity routed to no supplier — the
                  remainder stays orderable on its request.
                </p>
              ) : null}
            </div>
          )
        ) : null}

        {/* ── Step 3 · Summary ── */}
        {step === 2 ? (
          <div className="flex flex-col gap-4">
            {isEdit ? (
              <SummaryBlock
                heading={`${suppliers.find((s) => s.id === supplierId)?.name ?? 'Supplier'}`}
                total={editLines.reduce((sum, l) => sum + groupLineTotal(l), 0)}
                rows={editLines.map((l) => {
                  const source = editing.items.find((i) => i.id === l.key);

                  return {
                    key: l.key,
                    label: source?.itemIdentity ?? 'Line',
                    request: source?.purchaseRequestNumber ?? '',
                    quantity: l.orderedQuantity ?? 0,
                    unitCost: l.unitCost ?? 0,
                  };
                })}
              />
            ) : (
              <>
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="text-sm font-medium text-slate-800">
                    {orderable.length} order{orderable.length === 1 ? '' : 's'} to place
                  </h3>
                  <span className="font-mono text-xs tabular-nums text-slate-600">
                    {formatMoney(grandTotal)} total
                  </span>
                </div>

                {orderable.map((group, index) => (
                  <SummaryBlock
                    key={group.id}
                    heading={`Order ${index + 1} · ${
                      suppliers.find((s) => s.id === group.supplierId)?.name ?? 'Supplier'
                    }`}
                    total={groupTotal(group)}
                    rows={group.lines
                      .filter((l) => l.purchaseRequestItemId !== null)
                      .map((l) => {
                        const entry = poolByLineId.get(l.purchaseRequestItemId!);

                        return {
                          key: l.key,
                          label: entry?.line.itemIdentity ?? 'Line',
                          request: entry?.request.requestNumber ?? '',
                          quantity: l.orderedQuantity ?? 0,
                          unitCost: l.unitCost ?? 0,
                        };
                      })}
                  />
                ))}
              </>
            )}

            {/*
              WHAT HAPPENS ON SAVE, stated before the tick rather than appended to it. An order
              routed straight into the approval cycle is a different act from one held as a
              draft, and which of the two it is depends on a grant the reader may not hold.
            */}
            {isEdit ? (
              <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                The lines are rewritten and the quantities they hold against each request are
                adjusted in the same save.
              </p>
            ) : canSubmit ? (
              <div className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                {/*
                  THE SOURCE'S OWN WORD. Its tick reads "Skip draft", and the purchase request
                  beside it already says so — inventing a second wording for one concept across
                  two forms in one group is what the one-word rule exists to stop.
                */}
                <label
                  htmlFor={`${ID_PREFIX}-submit-now`}
                  className="flex items-start gap-2 text-sm text-slate-600"
                >
                  <Checkbox
                    id={`${ID_PREFIX}-submit-now`}
                    name={`${ID_PREFIX}-submit-now`}
                    checked={submitNow}
                    onChange={() => setSubmitNow((v) => !v)}
                    disabled={saving}
                  />
                  <span>Skip draft</span>
                </label>

                <p className="text-xs text-slate-600">
                  {submitNow
                    ? `${orderable.length === 1 ? 'The order goes' : 'Each order goes'} straight into the approval cycle. ${orderable.length === 1 ? 'It stops' : 'They stop'} being editable at that point.`
                    : `${orderable.length === 1 ? 'The order is' : 'The orders are'} held as a draft. Submitting is a separate action from the register.`}
                </p>
                <p className="text-xs text-slate-600">
                  Either way the quantities are committed against the source requests now, so
                  nothing else can order the same lines.
                </p>
              </div>
            ) : (
              <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-slate-700">
                {orderable.length === 1 ? 'The order is' : 'The orders are'} created as a draft —
                you do not have the grant to submit for approval, so an authorized reader submits
                from the register. The quantities are committed against the source requests either
                way.
              </p>
            )}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

function toLineInput(line: GroupLine & { purchaseRequestItemId: string }): PurchaseOrderLineInput {
  return {
    purchaseRequestItemId: line.purchaseRequestItemId,
    orderedQuantity: line.orderedQuantity ?? 0,
    unitCost: line.unitCost ?? 0,
  };
}

/** One still-orderable line, with the request that asked for it. */
interface PoolEntry {
  request: OrderablePurchaseRequest;
  line: OrderableRequestLine;
}

/**
 * Marks an option covering SEVERAL request lines. The suffix is the inventory item's id — the
 * merge is keyed on the id and never on the product's name, which is the call the stock-level
 * grouping already takes: two catalog rows can share a governed name.
 */
const MULTI_PREFIX = 'multi:';

/** What this order may still take across the sources one option covers. */
function totalAvailable(entries: PoolEntry[], capOf: (lineId: string) => number): number {
  return entries.reduce((sum, entry) => sum + capOf(entry.line.purchaseRequestItemId), 0);
}

function singleOption(entry: PoolEntry, available: number): SelectOption {
  return {
    value: entry.line.purchaseRequestItemId,
    label: entry.line.itemIdentity,
    sublabel: entry.request.requestNumber,
    right: String(available),
    keywords: `${entry.line.sku} ${entry.line.itemName} ${entry.request.requestNumber}`,
  };
}

function mergedOption(entries: PoolEntry[], capOf: (lineId: string) => number): SelectOption {
  // Deduped and ordered, so one request asking twice for an item does not read as two.
  const numbers = [...new Set(entries.map((e) => e.request.requestNumber))].sort();

  return {
    value: `${MULTI_PREFIX}${entries[0].line.inventoryItemId}`,
    label: entries[0].line.itemIdentity,
    sublabel: numbers.join(', '),
    right: String(totalAvailable(entries, capOf)),
    keywords: entries
      .map((e) => `${e.line.sku} ${e.line.itemName} ${e.request.requestNumber}`)
      .join(' '),
  };
}

/**
 * The request lines this row may pick, MERGED BY ITEM.
 *
 * **Product call, 01 Oct 2026, in the reader's own words: *if there are 2 PR, requesting same
 * item, merge them into 1 dropdown, but it must show the requesting PR nos. remove the quantity
 * left and unit. instead display on the outter most of the item name the total requested
 * quantity*.**
 *
 * - **The PR NUMBERS are the sublabel**, which is what makes a pooled list readable: two lines
 *   can name the same product, and the requests are the only thing telling them apart.
 * - **THE TRAILING SLOT IS WHAT THIS ORDER MAY STILL TAKE** — the request's remainder LESS what
 *   the other orders in this session have already taken — mono and tabular so a column of them
 *   lines up. **Product call, 01 Oct 2026**, correcting the requested total this shipped with:
 *   routing 30 of a 45-line to one supplier left the next supplier's picker still offering 45,
 *   which is a figure no order can be given. It is the SAME number the Qty cell caps at and the
 *   same one the merged pick seeds, so the three cannot disagree.
 * - **THIS ROW'S OWN CHOICE IS ALWAYS A SINGLE OPTION**, never folded into a merge. A merged
 *   entry's value is a synthetic `multi:` key, so a row holding one real request line would have
 *   nothing in the list matching its value and `<Select>` would render a blank trigger.
 */
function requestLineOptions(
  pool: PoolEntry[],
  group: OrderGroup,
  line: GroupLine,
  capOf: (lineId: string) => number,
): SelectOption[] {
  const own =
    line.purchaseRequestItemId === null
      ? null
      : (pool.find((e) => e.line.purchaseRequestItemId === line.purchaseRequestItemId) ?? null);

  const free = pool.filter(
    (entry) =>
      entry.line.purchaseRequestItemId !== line.purchaseRequestItemId
      && capOf(entry.line.purchaseRequestItemId) > 0
      && !group.lines.some(
        (l) => l.key !== line.key && l.purchaseRequestItemId === entry.line.purchaseRequestItemId,
      ),
  );

  const byItem = new Map<string, PoolEntry[]>();

  for (const entry of free) {
    const held = byItem.get(entry.line.inventoryItemId);

    if (held) held.push(entry);
    else byItem.set(entry.line.inventoryItemId, [entry]);
  }

  const merged = [...byItem.values()].map((entries) =>
    entries.length === 1
      ? singleOption(entries[0], capOf(entries[0].line.purchaseRequestItemId))
      : mergedOption(entries, capOf),
  );

  /*
    THIS ROW'S OWN LINE reads the cap too, and the cap EXCLUDES this group — so the figure is
    everything this order may hold of the line, including what the row already carries. That is
    exactly the Qty cell's own max, which is what keeps the picker and the cell from stating two
    different ceilings for one row.
  */
  return own === null
    ? merged
    : [singleOption(own, capOf(own.line.purchaseRequestItemId)), ...merged];
}

/** Narrows the request list in memory — the whole feed arrives, so the filter sees everything. */
function matches(request: OrderablePurchaseRequest, search: string): boolean {
  const term = search.trim().toLowerCase();
  if (term.length === 0) return true;

  return (
    request.requestNumber.toLowerCase().includes(term)
    || (request.items ?? '').toLowerCase().includes(term)
    || (request.categories ?? '').toLowerCase().includes(term)
    || request.lines.some((line: OrderableRequestLine) =>
      line.sku.toLowerCase().includes(term),
    )
  );
}

/** One order's lines on the summary, with its own total under them. */
function SummaryBlock({
  heading,
  total,
  rows,
}: {
  heading: string;
  total: number;
  rows: { key: string; label: string; request: string; quantity: number; unitCost: number }[];
}) {
  /*
    THE MERGED TOTAL, and it is the reader's own call of 29 Sep 2026: two requests asking for one
    item produce TWO lines, each keeping its trace back to the request that asked for it, and a
    merged row under them says what the order is actually for. It is drawn only where an item
    appears on MORE THAN ONE line — on one, the merged total is that line's and printing it would
    be the same value twice.
  */
  const merged = rows.reduce<Map<string, { label: string; quantity: number; total: number; lines: number }>>(
    (acc, row) => {
      const current = acc.get(row.label) ?? { label: row.label, quantity: 0, total: 0, lines: 0 };

      current.quantity += row.quantity;
      current.total += row.quantity * row.unitCost;
      current.lines += 1;
      acc.set(row.label, current);

      return acc;
    },
    new Map(),
  );

  const duplicated = [...merged.values()].filter((m) => m.lines > 1);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-slate-800">{heading}</span>
        <span className="font-mono text-xs tabular-nums text-slate-600">{formatMoney(total)}</span>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr className="text-[9px] uppercase tracking-wider text-gray-600 [&>th]:font-normal">
              {/*
                THE PR NUMBER MOVED UNDER THE ITEM, with the grid on the step before — 02 Oct
                2026. The recap has to be the same shape as the thing it recaps: merged there and
                a separate column here is a reader filling in one cell and reading two one click
                later, which is the one cost of that merge worth closing.
              */}
              <th className="px-3 py-2 text-left">Item</th>
              <th className="border-l border-slate-200 px-3 py-2 text-right">Qty</th>
              <th className="border-l border-slate-200 px-3 py-2 text-right">Unit cost</th>
              <th className="border-l border-slate-200 px-3 py-2 text-right">Line total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-t border-slate-200">
                <td className="px-3 py-2 align-middle text-slate-600">
                  <span className="block">{row.label}</span>
                  <span className="block font-mono text-[11px] leading-none text-slate-500">
                    {row.request || '—'}
                  </span>
                </td>
                <td className="border-l border-slate-200 px-3 py-2 text-right align-middle font-mono tabular-nums text-slate-600">
                  {row.quantity}
                </td>
                <td className="border-l border-slate-200 px-3 py-2 text-right align-middle font-mono tabular-nums text-slate-600">
                  {formatMoney(row.unitCost)}
                </td>
                <td className="border-l border-slate-200 px-3 py-2 text-right align-middle font-mono tabular-nums text-slate-600">
                  {formatMoney(row.quantity * row.unitCost)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-slate-200 bg-slate-50">
              {/* THREE, not four: the merge took a column out and a stale span would swallow
                  the figure's own cell. */}
              <td colSpan={3} className="px-3 py-2 text-right text-xs text-slate-500">
                Total
              </td>
              <td className="border-l border-slate-200 px-3 py-2 text-right font-mono tabular-nums text-slate-600">
                {formatMoney(total)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {duplicated.length > 0 ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <p className="mb-1 text-xs text-slate-500">
            Ordered from more than one request — the lines stay separate, the total is merged.
          </p>

          <ul className="flex flex-col gap-0.5">
            {duplicated.map((m) => (
              <li key={m.label} className="flex items-baseline justify-between gap-3 text-xs">
                <span className="text-slate-700">
                  {m.label} <CountBadge count={m.lines} />
                </span>
                <span className="font-mono tabular-nums text-slate-600">
                  {m.quantity} · {formatMoney(m.total)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
